import { Helmet } from "react-helmet-async";
import { CANONICAL_ORIGIN, canonicalUrl } from "@/lib/app-config";

export interface ArticleJsonLdProps {
  /** Site-relative path of the page, e.g. "/peptides/weight-loss" */
  path: string;
  /** Article headline — keep under 110 chars for rich-result eligibility */
  headline: string;
  description: string;
  /** ISO date (YYYY-MM-DD) the content was first published */
  datePublished: string;
  /** ISO date (YYYY-MM-DD) of the last meaningful content update */
  dateModified: string;
  /** Site-relative or absolute image URL representing the article */
  image?: string;
  /** Section / topical grouping, e.g. "Peptide Therapy" */
  section?: string;
  keywords?: string[];
  /** Set for gated or noindex pages */
  isAccessibleForFree?: boolean;
}

const absolute = (url: string) =>
  url.startsWith("http") ? url : `${CANONICAL_ORIGIN}${url.startsWith("/") ? url : `/${url}`}`;

/**
 * Emits a schema.org Article node for guide / library content, linked to the
 * sitewide Organization and WebSite entities declared in index.html.
 *
 * Deduplicated by @id so it never conflicts with a page's MedicalWebPage node.
 */
export const ArticleJsonLd = ({
  path,
  headline,
  description,
  datePublished,
  dateModified,
  image = "/og-image.jpg",
  section,
  keywords,
  isAccessibleForFree = true,
}: ArticleJsonLdProps) => {
  const url = canonicalUrl(path);

  const data = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": `${url}#article`,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    url,
    headline,
    description,
    inLanguage: "en-US",
    datePublished,
    dateModified,
    isAccessibleForFree,
    image: {
      "@type": "ImageObject",
      url: absolute(image),
      width: 1200,
      height: 630,
    },
    author: { "@id": `${CANONICAL_ORIGIN}/#organization` },
    publisher: { "@id": `${CANONICAL_ORIGIN}/#organization` },
    isPartOf: { "@id": `${CANONICAL_ORIGIN}/#website` },
    ...(section ? { articleSection: section } : {}),
    ...(keywords?.length ? { keywords: keywords.join(", ") } : {}),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

export default ArticleJsonLd;
